import React from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './TrialBanner.module.css';

// See LoginForm.tsx for the pattern — react-i18next's types require TS 5+,
// this project is on 4.9.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useTranslation = useTranslationUntyped as any;

type BannerVariant = 'trial' | 'founding' | 'pastDue';

export interface TrialBannerProps {
    /** Subscription status from the backend: Active=1, Expired=2, Cancelled=3, PastDue=4, Trial=5, Incomplete=6. */
    status: number;
    /** ISO-8601 date string — the end of the trial window. */
    trialEndAt?: string;
    /** Human-readable plan name (e.g. "Growth"). Falls back to tier if absent. */
    planName?: string;
    /** True when the trial was claimed before the 2026-12-31 Asia/Dubai founding cutoff. */
    isFoundingMember?: boolean;
    /** Where to send the user when they click the manage-billing CTA. */
    onManageBilling?: () => void;
}

/**
 * Trial/billing banner shown on the owner Dashboard and (conditionally) inside
 * the onboarding wizard header after a trial is claimed (PO-D-018).
 *
 * Status mapping:
 *   5 (Trial)      → blue gradient, "{plan} — {N} days remaining"
 *   6 (Incomplete) → blue gradient, "Finish adding your card to start your trial"
 *   4 (PastDue)    → red, "Payment failed — please update your card"
 *   (other)        → returns null (not rendered)
 *
 * If isFoundingMember is true on a Trial, the banner uses the gold "founding"
 * gradient and surfaces the 6-month-free badge.
 */
const TrialBanner: React.FC<TrialBannerProps> = ({
    status,
    trialEndAt,
    planName,
    isFoundingMember,
    onManageBilling,
}) => {
    const { t } = useTranslation('billing');
    const navigate = useNavigate();

    // PastDue — red banner
    if (status === 4) {
        return (
            <div className={`${styles.banner} ${styles['banner--pastDue']}`} role="alert">
                <span className={styles.icon} aria-hidden="true">!</span>
                <div className={styles.message}>
                    <p className={styles.title}>{t('dashboard.payment_past_due')}</p>
                </div>
                <button
                    type="button"
                    className={styles.action}
                    onClick={onManageBilling ?? (() => navigate('/subscription'))}
                >
                    {t('dashboard.manage_billing')}
                </button>
            </div>
        );
    }

    // Trial or Incomplete — blue/gold banner
    if (status !== 5 && status !== 6) return null;

    const variant: BannerVariant = isFoundingMember ? 'founding' : 'trial';
    const daysRemaining = computeDaysRemaining(trialEndAt);

    // Incomplete = setup_intent not confirmed yet — different copy
    const titleText =
        status === 6
            ? t('banner.trial_ends', {
                  plan: planName ?? '',
                  date: formatDate(trialEndAt),
              })
            : daysRemainingLabel(t, planName ?? '', daysRemaining);

    return (
        <div className={`${styles.banner} ${styles[`banner--${variant}`]}`} role="status">
            <span className={styles.icon} aria-hidden="true">{isFoundingMember ? '★' : '✓'}</span>
            <div className={styles.message}>
                <p className={styles.title}>
                    {titleText}
                    {isFoundingMember && (
                        <span className={styles.foundingBadge}>
                            {t('step6.founding_badge')}
                        </span>
                    )}
                </p>
                {status !== 6 && <p className={styles.subtitle}>{t('banner.no_charge_until')}</p>}
            </div>
            <button
                type="button"
                className={styles.action}
                onClick={onManageBilling ?? (() => navigate('/subscription'))}
            >
                {t('dashboard.manage_billing')}
            </button>
        </div>
    );
};

function computeDaysRemaining(isoDate?: string): number {
    if (!isoDate) return 0;
    const end = new Date(isoDate).getTime();
    const now = Date.now();
    const ms = end - now;
    return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function formatDate(isoDate?: string): string {
    if (!isoDate) return '';
    try {
        return new Date(isoDate).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return isoDate;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function daysRemainingLabel(t: any, planName: string, days: number): string {
    if (days === 1) {
        return t('dashboard.trial_remaining_one', { plan: planName });
    }
    if (days <= 0) {
        return t('dashboard.trial_ended');
    }
    return t('dashboard.trial_remaining', { plan: planName, days });
}

export default TrialBanner;
